import React from "react";
import { useSelector } from "react-redux";
import { Zap, FileText, MessageSquare, Users } from "lucide-react";

const UsageItem = ({ icon: Icon, label, used, limit, iconTone, barColor }) => {
  const isUnlimited = limit == null;
  const pct = isUnlimited ? 0 : limit === 0 ? 100 : Math.min((used / limit) * 100, 100);
  const isNearLimit = !isUnlimited && pct >= 80;

  return (
    <div className="flex items-center gap-3 rounded-[18px] border border-[#EEEEEE] bg-white px-3.5 py-3 shadow-[0_6px_18px_rgba(21,27,38,0.025)]">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${iconTone}`}>
        <Icon className="w-[17px] h-[17px]" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[13px] font-medium text-[#303236]">{label}</span>
          <span className="text-[12px] text-[#777C82] tabular-nums">
            {used.toLocaleString()}
            <span className="text-[#A0A4A8]">
              {" / "}
              {isUnlimited ? "Unlimited" : limit.toLocaleString()}
            </span>
          </span>
        </div>
        <div className="h-1.5 w-full bg-[#F1F3F3] rounded-full overflow-hidden">
          {!isUnlimited && (
            <div
              className="h-full rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${Math.max(pct, 1)}%`,
                backgroundColor: isNearLimit ? "#E3655B" : barColor,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const UsageSection = () => {
  const totalResponses = useSelector((s) => s.app.totalResponses);
  const aiUses = useSelector((s) => s.app.aiUses);
  const surveyCount = useSelector((s) => s.app.surveyCount);
  const seats = useSelector((s) => s.app.seats);
  const limits = useSelector((s) => s.app.usageLimits);
  const subscription = useSelector((s) => s.app.currentSubscription);

  if (!subscription) return null;

  return (
    <div className="rounded-[22px] border border-[#E8E8E8] bg-white/95 p-5 shadow-[0_8px_30px_rgba(21,27,38,0.035)]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-[#2A2D31]">Usage</h3>
          <p className="mt-1 text-[12px] text-[#777C82]">
            Monthly usage across your current workspace.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <UsageItem
          icon={FileText}
          label="Responses"
          used={totalResponses}
          limit={limits?.responses}
          iconTone="bg-[#F5F7F7] text-[#5E6268]"
          barColor="#8A8F94"
        />
        <UsageItem
          icon={Zap}
          label="AI Uses"
          used={aiUses}
          limit={limits?.ai}
          iconTone="bg-[#FFF7EE] text-[#B76B1D]"
          barColor="#D28A22"
        />
        <UsageItem
          icon={MessageSquare}
          label="Surveys"
          used={surveyCount}
          limit={limits?.surveys}
          iconTone="bg-[#F1FCF8] text-[#407463]"
          barColor="#8BBEAA"
        />
        <UsageItem
          icon={Users}
          label="Team Seats"
          used={seats?.used || 1}
          limit={seats?.limit}
          iconTone="bg-[#F7F9F8] text-[#5E6268]"
          barColor="#A7ADAD"
        />
      </div>
    </div>
  );
};

export default UsageSection;
